import React, { useState, useEffect } from "react";
import "./main.css";
import AspectRatioSelector from "../../../../components/AspectRatioSelector/AspectRatioSelector";
import PromptInput from "../../../../components/PromptInput/PromptInput";
import TagList from "../../../../components/TagList/TagList";
import { generateImage } from "../../../../api/api";
import { Buffer } from 'buffer';
import Notification from "../../../../components/notification";

const ImageGenerator = ({ selectedFormat, artFormats, onProfileUpdate }) => {
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [inputWords, setInputWords] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [selectedRatio, setSelectedRatio] = useState("1:1");
  const [error, setError] = useState(null);
  const [isSingleObject, setIsSingleObject] = useState(false);
  const [isCleanBackground, setIsCleanBackground] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [notification, setNotification] = useState(null);

  // Get available tags from the selected format
  const getAvailableTags = () => {
    if (artFormats && selectedFormat && artFormats[selectedFormat]) {
      return artFormats[selectedFormat].styleTags || [];
    }
    return [];
  };

  // Reset selected tags when format changes
  useEffect(() => {
    setSelectedTags([]);
  }, [selectedFormat]);

  function getPipeline() {
    console.log("selectedFormat", selectedFormat);
    switch (selectedFormat) {
      case "CLASSIC":
        return "CLASSIC";
      // case "ThreeD":
      //   return "THREE_D";
      default:
        return "CLASSIC";
    }
  }

  const getPlaceholderStyle = () => {
    const ratio = selectedRatio.split(":");
    const aspectRatio = parseInt(ratio[0]) / parseInt(ratio[1]);
    const baseHeight = 400;

    if (aspectRatio > 1) {
      return {
        width: "95%",
        height: `${baseHeight / aspectRatio}px`,
      };
    } else if (aspectRatio < 1) {
      return {
        width: `${baseHeight * aspectRatio}px`,
        height: `${baseHeight}px`,
        margin: "0 auto",
      };
    }
    return {
      width: `${baseHeight}px`,
      height: `${baseHeight}px`,
      margin: "0 auto",
    };
  };

  const getRatioType = (ratio) => {
    const [width, height] = ratio.split(":").map(Number);
    if (width === height) return "Square";
    if (width > height) return "Landscape";
    return "Portrait";
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsLoading(true);
      setGeneratedImage(null);

      // Modify prompt based on settings
      let Prompt = inputWords.join(", ");

      const generationData = {
        resolutionType: getRatioType(selectedRatio),
        prompt: Prompt,
        styleTags: selectedTags,
        pipeline: getPipeline(),
        // Additional parameters
        settings: {
          singleObject: isSingleObject,
          cleanBackground: isCleanBackground,
          isPublic: isPublic,
        },
      };

      const response = await generateImage(generationData);

      if (response.data && response.data.data) {
        const buffer = new Uint8Array(response.data.data.buffer.data);
        const base64String = Buffer.from(buffer).toString("base64");
        setGeneratedImage(`data:image/png;base64,${base64String}`);
        
        // Update profile after successful generation
        if (onProfileUpdate) {
          await onProfileUpdate();
        }
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      if (error.response?.status === 402) {
        setNotification({
          type: 'error',
          message: 'Insufficient credits. Please purchase more credits to continue generating images.'
        });
      } else if (error.response?.status === 400) {
        setError(
          error.response.data.message ||
            "Failed to generate image. Please try again."
        );
      } else if (error.response?.status === 401) {
        window.location.href = "/sign-in";
      } else {
        setError(error.message || "Failed to generate image. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement("a");
      link.href = generatedImage;
      link.download = "generated-image.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleRefresh = () => {
    setGeneratedImage(null);
    setError(null);
    setInputWords([]);
    setCurrentInput("");
    setSelectedTags([]);
  };

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line
  }, [selectedRatio]);

  return (
    <div className="imggen-image-generator">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="imggen-image-container">
        {isLoading ? (
          <div
            className="imggen-image-placeholder imggen-loading"
            style={getPlaceholderStyle()}
          >
            <div className="imggen-loading-spinner"></div>
            <p className="imggen-placeholder-text">Generating your image...</p>
          </div>
        ) : generatedImage ? (
          <div className="imggen-generated-image-wrapper">
            <div
              className="imggen-generated-image-container"
              style={getPlaceholderStyle()}
            >
              <button
                className="imggen-refresh-btn"
                onClick={handleRefresh}
                title="Refresh"
              >
                ↺
              </button>
              <img
                src={generatedImage}
                alt="Generated artwork"
                className="imggen-generated-image"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
            <div className="imggen-image-actions">
              <button className="imggen-action-btn imggen-download-btn" onClick={handleDownload}>
                Download Image
              </button>
            </div>
          </div>
        ) : (
          <div className="imggen-image-placeholder" style={getPlaceholderStyle()}>
            {error ? (
              <div className="imggen-placeholder-error">
                <button
                  className="imggen-refresh-btn"
                  onClick={handleRefresh}
                  title="Refresh"
                >
                  ↺
                </button>
                <p className="imggen-error-text">{error}</p>
                <p className="imggen-placeholder-text">Click to try again</p>
              </div>
            ) : (
              <p className="imggen-placeholder-text">
                Your generated image will appear here
              </p>
            )}
          </div>
        )}
      </div>

      <div className="imggen-options-divider"></div>

      <AspectRatioSelector
        selectedRatio={selectedRatio}
        setSelectedRatio={setSelectedRatio}
      />

      <PromptInput
        inputWords={inputWords}
        setInputWords={setInputWords}
        currentInput={currentInput}
        setCurrentInput={setCurrentInput}
      />

      <div className="imggen-image-controls">
        <div className="imggen-control-group">
          <label className="imggen-control-label">
            <input
              type="checkbox"
              checked={isSingleObject}
              onChange={(e) => setIsSingleObject(e.target.checked)}
              className="imggen-control-checkbox"
            />
            Single Object
          </label>
          <span className="imggen-control-description">
            Generate a single, centered object
          </span>
        </div>

        <div className="imggen-control-group">
          <label className="imggen-control-label">
            <input
              type="checkbox"
              checked={isCleanBackground}
              onChange={(e) => setIsCleanBackground(e.target.checked)}
              className="imggen-control-checkbox"
            />
            Clean Background
          </label>
          <span className="imggen-control-description">
            Generate with a clean, minimalist background
          </span>
        </div>

        <div className="imggen-control-group">
          <label className="imggen-control-label">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="imggen-control-checkbox"
            />
            Public
          </label>
          <span className="imggen-control-description">
            Make this image visible to other users (private generation costs 2 additional credits)
          </span>
        </div>
      </div>

      <TagList
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        isPanelOpen={isPanelOpen}
        setIsPanelOpen={setIsPanelOpen}
        availableTags={getAvailableTags()}
      />

      <button
        className="imggen-generate-button"
        onClick={handleGenerate}
        disabled={isLoading || inputWords.length === 0}
      >
        {isLoading ? "Generating..." : "Generate Image"}
      </button>
    </div>
  );
};

export default ImageGenerator;
